import { motion, useReducedMotion } from 'framer-motion';
import type { Icon } from '@phosphor-icons/react';

export interface IconCardItem {
  icon: Icon;
  title: string;
  description: string;
}

const EASE = [0.16, 1, 0.3, 1] as const;

/** Three-column grid of gold-icon cards that fade up on scroll. */
export function IconCardGrid({ items }: Readonly<{ items: readonly IconCardItem[] }>) {
  const reduced = useReducedMotion();

  return (
    <section className="py-16 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={reduced ? false : { opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {items.map((item, i) => {
            const ItemIcon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={reduced ? false : { opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: (i % 6) * 0.08, ease: EASE }}
                className="h-full"
              >
                {/* Framer Motion owns `transform` on the outer element and
                    leaves it inline after the entrance, which overrode a CSS
                    hover lift there and let its transition smear the entrance.
                    The lift lives on this inner element instead. */}
                <div className="h-full rounded-3xl bg-white/[0.03] border border-white/[0.08] p-8 transition-[transform,border-color] duration-300 motion-safe:hover:-translate-y-1 hover:border-slate-600">
                  <div className="w-14 h-14 rounded-2xl bg-slate-800/80 border border-slate-700/80 flex items-center justify-center mb-6">
                    <ItemIcon className="w-7 h-7 text-slate-200" weight="bold" />
                  </div>
                  <h3 className="section-h3 font-bold text-white mb-4">{item.title}</h3>
                  <p className="body-lg text-white/60 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
